import { useMutation, useQuery } from '@tanstack/react-query';
import { Avatar, Button, Form, Input, Modal, Pagination, Tooltip } from 'antd';
import { FormInstance } from 'antd/lib';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { fileExportApi, storageItemApi, storageProcessApi } from '../../../apis';
import { RequestManagementResponse, UpdatePreparingDoneDto } from '../../../apis/client-axios';
import { SvgAvatarDefault } from '../../../components/@svg/SvgAvatarDefault';
import SvgBlueCircle from '../../../components/@svg/SvgBlueCircle';
import SvgBoxDrop from '../../../components/@svg/SvgBoxDrop';
import SvgCoppy from '../../../components/@svg/SvgCheck';
import SvgCloseModalIcon from '../../../components/@svg/SvgCloseModalIcon';
import { SvgPrintIcon } from '../../../components/@svg/SvgPrintIcon';
import SvgIncrease from '../../../components/@svg/SvgScanIcon';
import { CustomCommonButton } from '../../../components/buttons/common/CustomButton';
import DatepickerCustom from '../../../components/DatepickerCustom';
import FormWrap from '../../../components/FormWrap';
import CustomSearchInput from '../../../components/input/CustomSearchInput';
import NotificationError from '../../../components/notifications/NotificationError';
import NotificationSuccess from '../../../components/notifications/NotificationSuccess';
import { RootState } from '../../../store';
import { QUERY_KEY, STATUS_ITEM_MANAGEMENT, statusChat, TIME_FORMAT } from '../../../util/constants';
import {
  formatDate,
  handleShowInternalDiscussStatus,
  Helper,
  renderStatus,
  renderStatusImage,
} from '../../../util/helper';
import './index.scss';
import { decode as decodeHtml } from 'he';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SvgMatch } from '../../../components/@svg/SvgMatch';
import { SvgNotMatch } from '../../../components/@svg/SvgNotMatch';
import CheckboxCustom from '../../../components/buttonCustom/CheckboxCustom';
import ButtonDetail from '../../../components/buttons/ButtonDetail';
import TableWrap from '../../../components/TableWrap';
import { ADMIN_ROUTE_PATH } from '../../../constants/router';
import CustomCheckbox from '../../../components/checkbox';
import { AmericaTimezone } from '../../../util/function-util';
import SvgArrowDown from '../../../components/@svg/SvgArrowDown';
import SvgFilterSort from '../../../components/@svg/SvgFilterSort';
import SimpleTooltip from './components/SimpleTooltip';
import DowloadButton from '../../../components/buttons/DowloadButtonWithArrrow';
import SvgImport from '../../../components/@svg/SvgImport';
import saveAs from 'file-saver';
import DownloadButtonWithArrow from '../../../components/buttons/DowloadButtonWithArrrow';
import { InchDisplay } from '../itemMangement/itemDetail/components/InchDisplay';
interface IItemDetail {
  sku: string;
  internalSku: string;
  productName: string;
  shipToBuyerItemId: number;
  labelUrl: string | undefined;
  battery: string;
  storageLength?: number;
  storageWidth?: number;
  storageHeight?: number;
  storageWeight?: number;
}

interface ICheckSku {
  empty: boolean;
  isMatch: boolean;
}

interface ITabAction {
  activeTab: number;
  fullTextSearch: string | undefined;
  checkboxValue: boolean;
  formattedStartDate: string | undefined;
  formattedEndDate: string | undefined;
  tab:
    | 'STOCK'
    | 'SHIP'
    | 'PHOTO_INIT'
    | 'PHOTO_REQUEST'
    | 'DISPOSAL'
    | 'QUESTION'
    | 'CHAT'
    | 'ALL'
    | 'WAITING_STOCK'
    | 'IN_STOCK'
    | 'ITEM_SHIP'
    | 'ITEM_DISPOSAL'
    | 'COMPLETE';
  sort: string;
}

type TColumnSort = 'ebayReturnsEbayReturnDeadline';
type TTypeSort = 'ASC' | 'DESC';

type TFilterSort = { columns: TColumnSort; sort: TTypeSort };

const n = (key: keyof UpdatePreparingDoneDto) => key;

const RequestManagement: React.FC = () => {
  const { authUser } = useSelector((state: RootState) => state.auth);
  const [form] = Form.useForm<FormInstance>();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [lstData, setLstData] = useState<RequestManagementResponse[]>([]);
  const [internalSkuInput, setInternalSkuInput] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showExportDropdown, setShowExportDropdown] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [enableApi, setEnableApi] = useState<boolean>(false);
  const [isCheckSku, setIsCheckSku] = useState<ICheckSku>({
    empty: true,
    isMatch: false,
  });
  const [checkPin, setCheckPin] = useState(false);
  const [isClickBtnPrint, setIsClickBtnPrint] = useState(false);
  const [filter, setFilter] = useState<TFilterSort>({ columns: null, sort: null });

  const navigate = useNavigate();
  const inputRef = useRef<any>(null);

  const [detailItem, setDetailItem] = useState<IItemDetail>({
    sku: '',
    internalSku: '',
    productName: '',
    shipToBuyerItemId: 0,
    labelUrl: '',
    battery: '',
  });
  const intl = useIntl();
  const role = authUser?.role || '';
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState<string>('');
  const refDebounceSearch = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    data: countRequest,
    refetch: refechCount,
    isFetching: isFetchingCount,
  } = useQuery({
    queryKey: [QUERY_KEY.COUNT_TAB_REQUEST, searchParams.get('tab')],
    staleTime: 0,
    queryFn: () => storageItemApi.storageItemControllerCountForRequestList(),
  });

  const exportExcel = useMutation({
    mutationKey: ['exportExcelRequest'],
    mutationFn: (type: string) => fileExportApi.fileExportControllerExportCsvRequest(type, { responseType: 'blob' }),
    onSuccess: ({ data }, type) => {
      const fileName = dayjs().format(TIME_FORMAT.MESSAGE_TIME);
      saveAs(
        data as any,
        type === STATUS_ITEM_MANAGEMENT.SHIPPING_PREPARING
          ? `$新規発送依頼_${fileName.replace(' ', '_')}.xlsx`
          : `$発送準備完了_${fileName.replace(' ', '_')}.xlsx`,
      );
    },
  });

  const handleDownloadNewShip = () => exportExcel.mutate(STATUS_ITEM_MANAGEMENT.SHIPPING_PREPARING);
  const handleDownloadPrepareDone = () => exportExcel.mutate(STATUS_ITEM_MANAGEMENT.SHIPPING_PREPARING_DONE);

  const tabTitles = useMemo(
    () => [
      {
        index: 0,
        name: intl.formatMessage({ id: 'request.inspection' }),
        text_scheduling: intl.formatMessage({ id: 'request.arrival_date' }),
        quantity: countRequest?.data?.stock,
        role: ['operation'],
        tab: 'STOCK',
      },
      {
        index: 1,
        name: intl.formatMessage({ id: 'request.warehousing_processing' }),
        text_scheduling: intl.formatMessage({ id: 'request.arrival_date' }),
        quantity: countRequest?.data?.stock,
        role: ['administrator'],
        tab: 'STOCK',
      },
      {
        index: 2,
        name: intl.formatMessage({ id: 'request.TAKE_BY_OPERATION' }),
        text_scheduling: intl.formatMessage({ id: 'request.date_photo_shot_rq' }),
        quantity: countRequest?.data?.photoInit,
        role: ['administrator', 'operation'],
        tab: 'PHOTO_INIT',
      },
      {
        index: 3,
        name: intl.formatMessage({ id: 'request.shipping' }),
        text_scheduling: intl.formatMessage({ id: 'request.request_ship' }),
        text_checkBox: intl.formatMessage({ id: 'request.new_shipping_requests' }),
        quantity: countRequest?.data?.ship,
        role: ['administrator', 'operation'],
        tab: 'SHIP',
      },
      {
        index: 4,
        name: intl.formatMessage({ id: 'request.additional_photo_shoot' }),
        text_scheduling: intl.formatMessage({ id: 'request.date_photo_shot_rq' }),
        text_checkBox: intl.formatMessage({ id: 'request.show_resolved' }),
        quantity: countRequest?.data?.photoRequest,
        role: ['administrator', 'operation'],
        tab: 'PHOTO_REQUEST',
      },
      {
        index: 5,
        name: intl.formatMessage({ id: 'request.image_video_upload' }),
        text_scheduling: intl.formatMessage({ id: 'request.arrival_date' }),
        quantity: countRequest?.data?.photoInit,
        role: ['staff'],
        tab: 'PHOTO_INIT',
      },
      {
        index: 6,
        name: intl.formatMessage({ id: 'request.upload_additional_photos' }),
        text_scheduling: intl.formatMessage({ id: 'request.date_photo_shot_rq' }),
        text_checkBox: intl.formatMessage({ id: 'request.show_resolved' }),
        quantity: countRequest?.data?.photoRequest,
        role: ['staff'],
        tab: 'PHOTO_REQUEST',
      },
      {
        index: 7,
        name: intl.formatMessage({ id: 'request.discard' }),
        text_scheduling: intl.formatMessage({ id: 'request.disposal_request_date' }),
        text_checkBox:
          role === 'staff'
            ? intl.formatMessage({ id: 'request.show_resolved' })
            : intl.formatMessage({ id: 'request.show_completed' }),
        quantity: countRequest?.data?.disposal,
        role: ['staff', 'administrator', 'operation'],
        tab: 'DISPOSAL',
      },
      {
        index: 8,
        name: intl.formatMessage({ id: 'request.inquiry' }),
        text_scheduling: intl.formatMessage({ id: 'request.mesage_sent_date' }),
        text_checkBox: intl.formatMessage({ id: 'request.show_completed_responses' }),
        quantity: countRequest?.data?.question,
        role: ['administrator', 'staff'],
        tab: 'QUESTION',
      },
      {
        index: 9,
        name: intl.formatMessage({ id: 'request.internal_message' }),
        text_scheduling: intl.formatMessage({ id: 'request.mesage_sent_date' }),
        text_checkBox: intl.formatMessage({ id: 'request.show_completed_responses' }),
        quantity: countRequest?.data?.chat,
        role: ['administrator', 'operation', 'staff'],
        tab: 'CHAT',
      },
    ],
    [countRequest?.data],
  );

  const filteredTabTitles = useMemo(
    () => tabTitles?.filter((tab) => tab.role.some((r) => r === role)),
    [role, tabTitles],
  );
  const [tabAction, setTabAction] = useState<ITabAction>({
    activeTab: 0,
    checkboxValue: false,
    fullTextSearch: undefined,
    formattedStartDate: undefined,
    formattedEndDate: undefined,
    tab: searchParams.get('tab') ? searchParams.get('tab') : (tabTitles[0].tab as any),
    sort: undefined,
  });
  const {
    data: storageItem,
    refetch: refetchStorageItem,
    isFetching: isFetchingStorageItem,
  } = useQuery({
    queryKey: [
      QUERY_KEY.STORAGE_ITEM,
      tabAction.activeTab,
      tabAction.checkboxValue,
      tabAction.fullTextSearch,
      tabAction.formattedStartDate,
      tabAction.formattedEndDate,
      tabAction.tab,
      pageSize,
      currentPage,
      filter.sort,
    ],
    enabled: !!authUser?.id,
    placeholderData: undefined,
    staleTime: 0,
    queryFn: async () => {
      if (role === 'administrator') {
        return await storageItemApi.storageItemControllerStorageManagementAdmin(
          currentPage,
          pageSize,
          filter.columns && filter.sort ? `${filter.columns}-${filter.sort}` : null,
          tabAction.fullTextSearch,
          undefined,
          tabAction.formattedStartDate,
          tabAction.formattedEndDate,
          undefined,
          searchParams.get('tab') ? searchParams.get('tab') : (tabAction.tab as any),
          tabAction.checkboxValue,
        );
      } else if (role === 'staff') {
        return await storageItemApi.storageItemControllerStorageManagementStaff(
          currentPage,
          pageSize,
          undefined,
          tabAction.fullTextSearch,
          undefined,
          tabAction.formattedStartDate,
          tabAction.formattedEndDate,
          undefined,
          searchParams.get('tab') ? searchParams.get('tab') : ('PHOTO_INIT' as any),
          tabAction.checkboxValue,
        );
      } else if (role === 'operation') {
        return await storageItemApi.storageItemControllerStorageManagementOperation(
          currentPage,
          pageSize,
          filter.columns && filter.sort ? `${filter.columns}-${filter.sort}` : null,
          tabAction.fullTextSearch,
          undefined,
          tabAction.formattedStartDate,
          tabAction.formattedEndDate,
          undefined,
          searchParams.get('tab') ? searchParams.get('tab') : (tabAction.tab as any),
          tabAction.checkboxValue,
        );
      }
      return null;
    },
  });

  const handleTabChange = (index) => {
    if (filteredTabTitles[tabAction.activeTab]?.index === index) return;
    setLstData([]);
    setCurrentPage(1);
    setSearchText('');
    refechCount();
    setTabAction({
      fullTextSearch: undefined,
      formattedStartDate: undefined,
      formattedEndDate: undefined,
      tab: searchParams.get('tab')
        ? searchParams.get('tab')
        : (filteredTabTitles.find((t) => t.index == index)?.tab as any),
      activeTab: filteredTabTitles.findIndex((t) => t.index == index),
      checkboxValue: false,
      sort: filter.sort,
    });
    setSearchParams({ ['tab']: filteredTabTitles.find((t) => t.index == index)?.tab });
  };

  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const handleShowSizeChange = (current, size) => {
    setCurrentPage(current);
    setPageSize(size);
  };

  const getTypeSort = (col: TColumnSort): TTypeSort | null =>
    filter.columns !== col ? 'DESC' : filter.sort === 'DESC' ? 'ASC' : null;

  const handleFilterSort = (col: TColumnSort, order: TTypeSort | null) => {
    setFilter({
      columns: order ? col : null,
      sort: order ?? null,
    });
    setCurrentPage(1);
  };

  const handleCopy = (text, textSuccess, textFailure) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        NotificationSuccess({ content: intl.formatMessage({ id: textSuccess }) });
      })
      .catch((err) => {
        NotificationError({ content: intl.formatMessage({ id: textFailure }) });
      });
  };
  const resetState = () => {
    setInternalSkuInput('');
    setIsCheckSku({
      empty: true,
      isMatch: false,
    });
    setIsClickBtnPrint(false);
    form.resetFields();
  };

  useEffect(() => {
    if (authUser && authUser.id) {
      setEnableApi(true);
    }
  }, [authUser]);

  useEffect(() => {
    if (searchParams && searchParams.get('tab')) {
      setTabAction({
        ...tabAction,
        tab: searchParams.get('tab') ? searchParams.get('tab') : (filteredTabTitles[0].tab as any),
        activeTab: filteredTabTitles.findIndex((item) => item.tab == searchParams.get('tab')) || 0,
      });
    } else {
      const find = filteredTabTitles.findIndex((item) => item.tab == searchParams.get('tab'));
      setTabAction({
        ...tabAction,
        tab: tabTitles[0].tab as any,
        activeTab: find > 0 ? find : 0,
      });
    }
  }, [searchParams, filteredTabTitles, tabTitles]);

  const activeTabIndex = filteredTabTitles[tabAction.activeTab]?.index;
  const showShippingPreparing = [3].includes(activeTabIndex);
  const showNotification = [8, 9].includes(activeTabIndex);
  const showRemarks = [0, 1, 2, 3, 4, 5, 6, 7].includes(activeTabIndex);
  const showInquiryDate = [8].includes(activeTabIndex);
  const showTrackingNumber = [0, 1, 5].includes(activeTabIndex);
  const showDeadlineRefund = [0, 1].includes(activeTabIndex);

  const columns = [
    showShippingPreparing && {
      title: '',
      key: 'action',
      className: 'col_9',
      width: 60,
      render: (item) =>
        renderStatus(item) === STATUS_ITEM_MANAGEMENT.SHIPPING_PREPARING ? (
          <Button
            onClick={() => {
              setIsOpen(true);
              setDetailItem({
                sku: item.itemSku,
                internalSku: item.internalSku,
                productName: decodeHtml(item.itemName || ''),
                shipToBuyerItemId: item.shipToBuyerItem.id,
                labelUrl: item.shipToBuyerItem.shipToBuyerRequest.shippoShippingLabel.labelUrl ?? undefined,
                battery: item.shipToBuyerItem.shipToBuyerRequest.battery,
                storageLength: item.storageLength,
                storageWidth: item.storageWidth,
                storageHeight: item.storageHeight,
                storageWeight: item.storageWeight,
              });
              resetState();
            }}
            className="request-management__popup-shippng"
          >
            <SvgBoxDrop />
          </Button>
        ) : null,
    },
    {
      title: '',
      key: 'detail',
      className: `col_10 ${[8, 9].includes(activeTabIndex) ? 'padding-right-unset' : ''}`,
      width: [8, 9].includes(activeTabIndex) ? 68 : 100,
      render: (item) => (
        <ButtonDetail
          content={intl.formatMessage({ id: 'item.detail' }) + ' '}
          buttonProps={{
            onClick: (e) => {
              if (e.ctrlKey || e.metaKey) {
                window.open(`${ADMIN_ROUTE_PATH.ITEM_MANAGEMENT_DETAIL}/${item?.id}`, '_blank');
              } else {
                navigate(`${ADMIN_ROUTE_PATH.ITEM_MANAGEMENT_DETAIL}/${item?.id}`);
              }
            },
          }}
        />
      ),
    },
    showNotification && {
      title: '',
      key: 'notification',
      className: 'col_11',
      width: 32,
      render: (item) => (
        <div className="width-20 height-20">
          {activeTabIndex === 9 &&
            item?.itemMessageGroup?.itemInternalMessages &&
            !item?.itemMessageGroup?.itemInternalMessages?.internalMessageReadHistories?.id && <SvgBlueCircle />}
          {activeTabIndex === 8 &&
            item?.itemMessageGroup?.itemInquiryMessages &&
            item?.itemMessageGroup?.itemInquiryMessages?.sendUserId &&
            item?.itemMessageGroup?.itemInquiryMessages?.isRead === 0 && <SvgBlueCircle />}
        </div>
      ),
    },
    {
      title: intl.formatMessage({ id: 'request.SKU' }),
      dataIndex: 'internalSku',
      key: 'sku',
      className: 'col_1',
      width: [7, 9].includes(activeTabIndex) ? 180 : 160,
      render: (text, item) => (
        <div className="request-management__col-copy">
          <Button
            className="request-management__btn-copy"
            onClick={() =>
              handleCopy(
                item.internalSku,
                intl.formatMessage({ id: 'common.copy' }),
                intl.formatMessage({ id: 'common.dont_copy' }),
              )
            }
          >
            <SvgCoppy />
          </Button>
          <p>{item.internalSku}</p>
        </div>
      ),
    },
    {
      title: intl.formatMessage({ id: 'request.customer' }),
      dataIndex: 'customer',
      key: 'customer',
      className: 'col_2',
      width: activeTabIndex == 3 ? 140 : activeTabIndex == 9 ? 200 : [7, 8].includes(activeTabIndex) ? 180 : 136,
      onCell: (_, index) => {
        if ([0, 1, 2, 5].includes(activeTabIndex)) {
          if (_.isFirstItem) {
            return { rowSpan: _.count };
          } else {
            return { rowSpan: 0 };
          }
        }
      },
      render: (_, item) =>
        [0, 1, 2, 5].includes(activeTabIndex) ? (
          <div className={`${item.count > 1 ? 'diff' : ''}`}>
            <p className="text-1-line">{item.isFirstItem && item.storageRequest?.user?.name}</p>
            <span>{item.isFirstItem && item.storageRequest?.user?.userCode}</span>
          </div>
        ) : (
          <div>
            <p className="text-1-line">{item.storageRequest?.user?.name}</p>
            <span>{item.storageRequest?.user?.userCode}</span>
          </div>
        ),
    },
    {
      title: intl.formatMessage({ id: 'request.product_name' }),
      dataIndex: 'itemName',
      key: 'productName',
      className: 'col_product-name',
      width:
        activeTabIndex == 3
          ? 280
          : activeTabIndex == 9
            ? 652
            : activeTabIndex == 5
              ? 376
              : activeTabIndex == 7
                ? 430
                : activeTabIndex == 8
                  ? 732
                  : 396,
      render: (text, item) => <p className="text-2-line break-word">{decodeHtml(item.itemName || '')}</p>,
    },
    {
      title: intl.formatMessage({ id: 'request.situation' }),
      dataIndex: 'situation',
      key: 'situation',
      className: `col_4 ${[9].includes(activeTabIndex) ? 'padding-left-unset' : ''} ${[7].includes(activeTabIndex) ? 'disposal-padding' : ''}`,
      width: [3].includes(activeTabIndex)
        ? 120
        : [8, 9].includes(activeTabIndex)
          ? 160
          : [7].includes(activeTabIndex)
            ? 224
            : 128,
      render: (text, item) => {
        const inquiryStatus: { [key in (typeof statusChat)[number]]: string } = {
          OPEN: intl.formatMessage({ id: 'chat-item.status.open' }),
          CLOSED: intl.formatMessage({ id: 'chat-item.status.closed' }),
          WAITING_CUSTOMER: intl.formatMessage({ id: 'chat-item.status.inProgress' }),
        };
        if ([0, 1, 2, 3, 5, 7].includes(activeTabIndex)) {
          return (
            <p>
              {intl.formatMessage({ id: `request.${renderStatus(item)}` })}
              {item.status === STATUS_ITEM_MANAGEMENT.IN_STORAGE && item.storageItemRacks.length > 0
                ? `_${item.storageItemRacks[0]?.rack?.code}`
                : ''}
            </p>
          );
        } else if (activeTabIndex === 4) {
          return <p>{intl.formatMessage({ id: `request.${renderStatusImage(item.takePhotoRequest?.status)}` })}</p>;
        } else if (activeTabIndex === 8) {
          return <p>{inquiryStatus[item?.inquiryStatus]}</p>;
        } else if (activeTabIndex === 9) {
          return (
            <p>
              {handleShowInternalDiscussStatus({
                status: item?.internalDiscussStatus,
                isRead: item?.itemMessageGroup?.itemInternalMessages?.internalMessageReadHistories?.id ? 1 : 0,
                authUserId: authUser?.id,
                sendBy: item?.itemMessageGroup?.itemInternalMessages?.sendStaffId,
                intlMultiLang: intl,
              })}
            </p>
          );
        } else if (activeTabIndex === 6) {
          return <p>{intl.formatMessage({ id: `request.${renderStatusImage(item.takePhotoRequest?.status)}` })}</p>;
        }
      },
    },
    showTrackingNumber && {
      title: intl.formatMessage({ id: 'request.tracking_number' }),
      key: 'tracking_number',
      className: 'col_14',
      width: 208,
      render: (item) => {
        const tracking = item?.shippoShippingLabel?.trackingNumber ?? item?.storageRequest?.trackingNumberProvided;

        const formatTracking = (str) => {
          if (!str) return '';
          const part1 = str.slice(0, 10);
          const part2 = str.slice(10, 14);
          const part3 = str.slice(14);
          return `${part1} ${part2} ${part3}`.trim();
        };

        return <p>{formatTracking(tracking)}</p>;
      },
    },
    showShippingPreparing && {
      title: intl.formatMessage({ id: 'request.rackno' }),
      key: 'rack',
      className: 'col_12',
      width: 136,
      render: (item) => <p>{item.storageItemRacks.length > 0 && item.storageItemRacks[0]?.rack?.code}</p>,
    },
    showShippingPreparing && {
      title: intl.formatMessage({ id: 'request.carrier' }),
      key: 'carrier',
      className: 'col_13',
      width: 100,
      render: (item) => <p>{item?.shipToBuyerItem?.shipToBuyerRequest?.shippingCarrier}</p>,
    },
    [9].includes(activeTabIndex) && {
      title: intl.formatMessage({ id: 'request.manager' }),
      key: 'manager',
      className: `col_5 ${[9].includes(activeTabIndex) ? 'padding-left-unset' : ''}`,
      width: 160,
      render: (item) => (
        <Avatar
          src={
            item?.itemMessageGroup?.assign?.avatar?.preview ? (
              Helper.getSourceFile(item?.itemMessageGroup?.assign?.avatar?.preview)
            ) : (
              <SvgAvatarDefault />
            )
          }
        />
      ),
    },
    [9].includes(activeTabIndex) && {
      title: intl.formatMessage({ id: 'request.date_send_new' }),
      key: 'dateSendNew',
      className: `col_6 ${[9].includes(activeTabIndex) ? '' : ''}`,
      width: 168,
      render: (item) => <p>{formatDate(item?.itemMessageGroup?.itemInternalMessages?.createdAt)}</p>,
    },
    [0, 1, 2, 5].includes(activeTabIndex) && {
      title: intl.formatMessage({ id: 'request.arrival_date' }),
      key: 'arrivalDate',
      className: 'col_7_1',
      width: 132,
      onCell: (_, index) => {
        if ([0, 1, 2, 5].includes(activeTabIndex)) {
          if (_.isFirstItem) {
            return { rowSpan: _.count };
          } else {
            return { rowSpan: 0 };
          }
        }
      },
      render: (item) =>
        [0, 1, 2, 5].includes(activeTabIndex) ? (
          <div className={`${item.count > 1 ? 'diff' : ''}`}>
            <p>{formatDate(item.isFirstItem && item.storageRequest?.deliveredAt)}</p>
          </div>
        ) : (
          <p>{formatDate(item.storageRequest?.deliveredAt)}</p>
        ),
    },

    [4, 6].includes(activeTabIndex) && {
      title: intl.formatMessage({ id: 'request.date_photo_shot_rq' }),
      key: 'datePhotoShot',
      className: 'col_7_2',
      width: 160,
      render: (item) => <p>{formatDate(item.takePhotoRequest?.createdAt)}</p>,
    },

    showShippingPreparing && {
      title: intl.formatMessage({ id: 'request.request_ship' }),
      key: 'requestShipDate',
      className: 'col_7_3',
      width: activeTabIndex != 3 ? 160 : 112,
      render: (item) => <p>{formatDate(item.shipToBuyerItem?.shipToBuyerRequest?.createdAt)}</p>,
    },

    showShippingPreparing && {
      title: intl.formatMessage({ id: 'request.deadline' }),
      key: 'deadline',
      className: 'col_3',
      width: 172,
      render: (text, item) => {
        return (
          <p className="text-2-line break-word">
            {item?.shipToBuyerItem?.shipToBuyerRequest?.shipByDate
              ? moment(item?.shipToBuyerItem?.shipToBuyerRequest?.shipByDate)
                  .tz(AmericaTimezone)
                  .format(`YYYY/MM/DD HH:mm z`)
              : ''}
          </p>
        );
      },
    },
    [7].includes(activeTabIndex) && {
      title: intl.formatMessage({ id: 'request.disposal_request_date' }),
      key: 'disposalRequestDate',
      className: `col_7_4 ${[7].includes(activeTabIndex) ? 'disposal-padding' : ''}`,
      width: [7].includes(activeTabIndex) ? 120 : 160,
      render: (item) => <p>{formatDate(item.disposalRequest?.createdAt)}</p>,
    },

    showInquiryDate && {
      title: intl.formatMessage({ id: 'request.date_send_new' }),
      key: 'dateSendNew',
      className: 'col_7_5',
      width: 160,
      render: (item) => <p>{formatDate(item.itemMessageGroup?.itemInquiryMessages?.createdAt)}</p>,
    },
    showDeadlineRefund && {
      title: () => {
        const columns: TColumnSort = 'ebayReturnsEbayReturnDeadline';
        return (
          <div className="item-management__table-sort">
            <div>{intl.formatMessage({ id: 'request.deadline_refund' })}</div>
            <div className="" onClick={() => handleFilterSort(columns, getTypeSort(columns))}>
              {filter.columns === columns ? (
                <div className={`${filter.sort === 'DESC' ? 'rotate-reverse' : ''}`}>
                  <SvgArrowDown />
                </div>
              ) : (
                <div>
                  <SvgFilterSort />
                </div>
              )}
            </div>
          </div>
        );
      },
      width: 128,
      key: 'ebayReturnsEbayReturnDeadline',
      className: 'col_15',
      render: (item) => {
        return (
          <>
            <span>
              {item?.ebayReturns?.ebayReturnDeadline
                ? moment(item?.ebayReturns?.ebayReturnDeadline).tz(AmericaTimezone).format(`YYYY/MM/DD HH:mm z`)
                : ''}
            </span>
          </>
        );
      },
    },
    showRemarks && {
      title: intl.formatMessage({ id: 'request.remarks' }),
      key: 'remarks',
      className: 'col_8',
      width: activeTabIndex == 3 ? 260 : [7].includes(activeTabIndex) ? 402 : 380,
      render: (item) => {
        const content = item?.storageItemNote?.content ?? '';
        if (!content) return null;

        return (
          <SimpleTooltip content={content} className={activeTabIndex === 3 ? 'width-diff' : ''}>
            <p className="text-2-line break-word text-pre-line cursor-pointer">{content}</p>
          </SimpleTooltip>
        );
      },
    },
  ].filter(Boolean);

  const isSkuSearch = (text?: string) => {
    if (!text) return false;
    return /^[A-Z0-9]{7}-[A-Z0-9]+$/.test(text.trim());
  };

  const keyword = tabAction.fullTextSearch?.trim();
  const isShippingTab = tabAction.tab === 'SHIP';
  const isSku = isShippingTab && isSkuSearch(keyword);

  let content = storageItem?.data?.content ?? [];

  if (isSku && keyword) {
    content = content.filter(
      (item) =>
        (item?.storageItem?.internalSku ?? item?.storageItems?.[0]?.internalSku)?.toUpperCase() ===
        keyword.toUpperCase(),
    );
  }

  const resultLstData = (content ?? []).flatMap((item, index) => {
    return item?.storageItems.map((storageItem, storageIndex) => ({
      ...storageItem,
      isFirstItem: storageIndex === 0,
      count: item?.storageItems?.length,
      parentId: index ? index : null,
      rowKey: storageItem?.id ? String(storageItem.id) : `${index}-${storageIndex}`,
    }));
  });

  const renderTableContent = () => {
    return (
      <div className="request-management_table">
        <div className="request-management_table_1_wrap">
          <TableWrap
            className=""
            columns={columns}
            data={resultLstData}
            rowKey="rowKey"
            isScroll
            scrollValue={{ y: '53vh' }}
            rowClassName={(_, index) => (_.parentId % 2 === 0 ? 'even-row' : 'odd-row')}
            rowHoverable={false}
            isLoading={isFetchingCount || isFetchingStorageItem}
          />
        </div>
        <div className="request-management__table-pagination-container">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={storageItem?.data?.total}
            showSizeChanger
            pageSizeOptions={[20, 50, 100]}
            locale={{ items_per_page: intl.formatMessage({ id: 'item.lines' }) }}
            onChange={handlePageChange}
            onShowSizeChange={handleShowSizeChange}
          />
        </div>
      </div>
    );
  };
  const handleCheckboxChange = (e) => {
    setLstData([]);
    setTabAction({
      ...tabAction,
      checkboxValue: e,
    });
    setCurrentPage(1);
  };

  const onFinish = (values: UpdatePreparingDoneDto) => {
    if (values) {
      processPreparingDone.mutate({ shipToBuyerItemId: detailItem.shipToBuyerItemId, internalSku: values.internalSku });
    }
  };

  const onFinishFailed = (values) => {
    NotificationError({
      content: intl.formatMessage({ id: 'request.bar_code_error' }),
    });
  };

  const processPreparingDone = useMutation({
    mutationFn: (payload: UpdatePreparingDoneDto) =>
      storageProcessApi.storageProcessControllerSetShipToBuyPreparingDone(payload),
    onSuccess: () => {
      NotificationSuccess({ content: intl.formatMessage({ id: 'common.updateSuccess' }) });
      refetchStorageItem();
      setIsCheckSku({
        empty: true,
        isMatch: false,
      });
      setIsOpen(false);
    },
    onError: (error: any) => {},
  });

  const checkProcessPreparingDone = useMutation({
    mutationFn: (payload: UpdatePreparingDoneDto) =>
      storageProcessApi.storageProcessControllerCheckSetPreparingDone(payload),
    onSuccess: (res: any) => {
      if (res.data.result) {
        setIsCheckSku({
          empty: false,
          isMatch: true,
        });
      } else {
        setIsCheckSku({
          empty: false,
          isMatch: false,
        });
      }
    },
    onError: (error: any) => {},
  });
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      checkProcessPreparingDone.mutate({
        shipToBuyerItemId: detailItem.shipToBuyerItemId,
        internalSku: internalSkuInput,
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      setCheckPin(false);
      setTimeout(() => {
        inputRef.current.focus();
      }, 200);
    }
  }, [isOpen]);

  useEffect(() => {
    if (refDebounceSearch.current) clearTimeout(refDebounceSearch.current);

    refDebounceSearch.current = setTimeout(() => {
      const v = searchText.trim();

      setCurrentPage(1);
      setTabAction((prev) => ({
        ...prev,
        fullTextSearch: v || undefined,
      }));
    }, 1000);
    return () => {
      if (refDebounceSearch.current) clearTimeout(refDebounceSearch.current);
    };
  }, [searchText]);

  const disabledDateEnd = (current) => {
    if (!!tabAction.formattedStartDate) {
      return current && current < dayjs(tabAction.formattedStartDate).startOf('day');
    } else {
      return undefined;
    }
  };
  const disabledDateStart = (current) => {
    if (!!tabAction.formattedEndDate) {
      return current && current > dayjs(tabAction.formattedEndDate).startOf('day');
    } else {
      return undefined;
    }
  };

  return (
    <div className="request-management">
      <div className="request-management_container">
        <div className="item-management__header">
          <span className="color-title font-weight-600 font-size-26">
            {intl.formatMessage({ id: 'request.title' })}
          </span>
          <div className="right">
            {tabAction.activeTab === 2 && (
              <div className="item-management__btn-download">
                <DownloadButtonWithArrow onClick={() => setShowExportDropdown(!showExportDropdown)} />
                {showExportDropdown && (
                  <div className="item-management__export-dropdown">
                    <div
                      className="item-management__export-option"
                      onClick={() => {
                        handleDownloadNewShip();
                        setShowExportDropdown(false);
                      }}
                    >
                      新規発送依頼
                    </div>
                    <div
                      className="item-management__export-option"
                      onClick={() => {
                        handleDownloadPrepareDone();
                        setShowExportDropdown(false);
                      }}
                    >
                      発送準備完了
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="request-management_tab-title">
          {filteredTabTitles.map((tab) => {
            return (
              <div
                key={tab?.index}
                className={`request-management_tab-title_item width_${role} ${
                  filteredTabTitles[tabAction.activeTab]?.index === tab?.index ? 'active' : ''
                }`}
                onClick={() => handleTabChange(tab?.index)}
              >
                <p className="request-management_tab-title_item_name request-management_tab-title_item-hover">
                  {tab.name}
                </p>
                <span className="request-management_tab-title_item_quantity">{tab.quantity}</span>
              </div>
            );
          })}
        </div>
        <div className="request-management_filter">
          <div className="request-management_filter_date-wrap_date">
            <p className="request-management_filter_date-wrap_date_text m-r-4">
              {filteredTabTitles[tabAction.activeTab]?.text_scheduling}
            </p>
            <DatepickerCustom
              datePickerProp={{
                onChange: (_, dateString: string) => {
                  setTabAction({
                    ...tabAction,
                    formattedStartDate: dateString,
                  });
                  setCurrentPage(1);
                },
                disabledDate: disabledDateStart,
                value: tabAction.formattedStartDate ? dayjs(tabAction.formattedStartDate) : undefined,
              }}
            />
            <span>~</span>
            <DatepickerCustom
              datePickerProp={{
                onChange: (_, dateString: string) => {
                  setTabAction({
                    ...tabAction,
                    formattedEndDate: dateString,
                  });
                  setCurrentPage(1);
                },
                disabledDate: disabledDateEnd,
                value: tabAction.formattedEndDate ? dayjs(tabAction.formattedEndDate) : undefined,
              }}
            />
          </div>
          {filteredTabTitles[tabAction.activeTab]?.text_checkBox && (
            <div className="request-management_filter_checkbox">
              <CheckboxCustom
                checked={tabAction.checkboxValue}
                onChange={handleCheckboxChange}
                content={filteredTabTitles[tabAction.activeTab].text_checkBox}
              />
            </div>
          )}

          <div>
            <CustomSearchInput
              className="font-weight-500 font-size-16 switch-false request-management__search-input"
              placeholder={intl.formatMessage({ id: 'item.searchPlaceholder' })}
              value={searchText}
              onChange={({ target: { value } }) => setSearchText(value)}
            />
          </div>
        </div>
        <div className="request-management_table">{renderTableContent()}</div>
      </div>
      <Modal
        title={intl.formatMessage({ id: 'requestShipping.title' })}
        closable={true}
        open={isOpen}
        centered={true}
        onCancel={() => setIsOpen(false)}
        className={'request-management__modal-shipping'}
        closeIcon={<SvgCloseModalIcon />}
        footer={
          <div className="request-management__btn-container">
            <CustomCommonButton
              isCancelButton={true}
              buttonProps={{
                className: 'request-management__btn-print',
                onClick: () => {
                  if (detailItem.labelUrl) {
                    window.open(detailItem.labelUrl, '_blank');
                    setIsClickBtnPrint(true);
                  }
                },
                disabled: !isCheckSku.isMatch || processPreparingDone.isPending || !detailItem.labelUrl,
              }}
              content={
                <p>
                  <SvgPrintIcon />
                  {intl.formatMessage({ id: 'common.print' })}
                </p>
              }
            />
            <CustomCommonButton
              isCancelButton={true}
              buttonProps={{
                disabled:
                  !isCheckSku.isMatch ||
                  processPreparingDone.isPending ||
                  (detailItem.battery && !checkPin) ||
                  !isClickBtnPrint,
                loading: processPreparingDone.isPending,
                className: 'request-management__btn-cancel',
                type: 'primary',
                onClick: () => form.submit(),
              }}
              content={intl.formatMessage({ id: 'common.ready' })}
            />
          </div>
        }
      >
        <div className="request-management__product-info">
          <p>{intl.formatMessage({ id: 'requestShipping.productInfo' })}</p>
        </div>
        <div className="request-management__gird-container">
          <div className="request-management__row-container">
            <div className="request-management__col-left-container">
              <span>SKU</span>
            </div>
            <div className="request-management__col-right-container">
              <span>{detailItem.internalSku}</span>
            </div>
          </div>
          <div className="request-management__row-container">
            <div className="request-management__col-left-container">
              <span>{intl.formatMessage({ id: 'requestShipping.customerSKU' })}</span>
            </div>
            <div className="request-management__col-right-container">
              <span>{detailItem.sku}</span>
            </div>
          </div>
          <div className="request-management__row-container">
            <div className="request-management__col-left-container">
              <span>{intl.formatMessage({ id: 'requestShipping.productName' })}</span>
            </div>
            <div className="request-management__col-right-container">
              <p className="text-1-line">{detailItem.productName}</p>
            </div>
          </div>
          <div className="request-management__storage-size-block">
            <h6 className="request-management__storage-size-title">
              {intl.formatMessage({ id: 'item.detail.storageSize' })}
            </h6>
          </div>
          <div className="request-management__row-container">
            <div className="request-management__storage-size-values">
              <span className="request-management__storage-size-item">
                {intl.formatMessage({ id: 'item.detail.length' })}
                <span className="request-management__storage-size-colon">:</span>{' '}
                <InchDisplay cm={detailItem.storageLength ?? 0} rounding="ceil" />
              </span>
              <span className="request-management__storage-size-item">
                {intl.formatMessage({ id: 'item.detail.width' })}
                <span className="request-management__storage-size-colon">:</span>{' '}
                <InchDisplay cm={detailItem.storageWidth ?? 0} rounding="ceil" />
              </span>
              <span className="request-management__storage-size-item">
                {intl.formatMessage({ id: 'item.detail.height' })}
                <span className="request-management__storage-size-colon">:</span>{' '}
                <InchDisplay cm={detailItem.storageHeight ?? 0} rounding="ceil" />
              </span>
              <span className="request-management__storage-size-item">
                {intl.formatMessage({ id: 'item.detail.storageWeight' })}
                <span className="request-management__storage-size-colon">:</span> {detailItem.storageWeight ?? 0} kg
              </span>
            </div>
          </div>
        </div>
        <FormWrap
          form={form}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          className="request-management__form-request-shiping"
        >
          <Form.Item
            name={n('internalSku')}
            className="request-management__product-bar-code m-b-12"
            label={
              <p className="font-size-16">
                {intl.formatMessage({ id: 'requestShipping.productBarcode' })}
                <span className="request-management__require">*</span>
              </p>
            }
            required
            rules={[{ required: true, message: '' }]}
          >
            <Input
              suffix={<SvgIncrease />}
              autoFocus={true}
              className="font-size-16"
              ref={inputRef}
              placeholder={intl.formatMessage({ id: 'request.bar_code_placeholder' })}
              onChange={(e) => {
                const value = e.target.value ?? '';
                setInternalSkuInput(value);
                if (value == '') {
                  resetState();
                }
              }}
              onKeyDown={handleKeyDown}
            />
          </Form.Item>
          <Form.Item className="m-b-12">
            <CustomCommonButton
              isCancelButton={true}
              buttonProps={{
                className: 'request-management__btn-confirm font-weight-600',
                onClick: () => {
                  if (internalSkuInput.trim().length > 0) {
                    checkProcessPreparingDone.mutate({
                      shipToBuyerItemId: detailItem.shipToBuyerItemId,
                      internalSku: internalSkuInput.trim(),
                    });
                  } else {
                    form.validateFields();
                  }
                },
              }}
              content={intl.formatMessage({ id: 'request.ship_ready.check' })}
            />
          </Form.Item>
        </FormWrap>
        <div className="request-management__judgement-container">
          <div className="request-management__judgement-title">
            {isCheckSku.empty ? (
              <></>
            ) : (
              <p>
                {intl.formatMessage({ id: 'requestShipping.judgement' })}
                <span>&nbsp;:</span>
              </p>
            )}
          </div>
          <div className="request-management__judgement-description">
            {isCheckSku.empty ? (
              <></>
            ) : (
              <p className="request-management__status-sku">
                {isCheckSku.isMatch ? (
                  <>
                    <span>{intl.formatMessage({ id: 'requestShipping.match' })}</span>
                    <SvgMatch />
                  </>
                ) : (
                  <>
                    {' '}
                    <span>{intl.formatMessage({ id: 'requestShipping.notMatch' })}</span>
                    <SvgNotMatch />
                  </>
                )}
              </p>
            )}
          </div>
        </div>
        {detailItem.battery ? (
          <div className="request-management__pin">
            <CustomCheckbox
              name="pin"
              checked={checkPin}
              onChange={({ target: { checked } }) => setCheckPin(checked)}
            />
            <p>
              以下のUNラベルを貼付しました
              <br />
              {detailItem.battery.match(/UN\d+/g)}
            </p>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default RequestManagement;
